import React, {
  useState,
  useEffect,
  useCallback,
  useMemo,
  useRef,
} from 'react';
import { formatInTimeZone } from 'date-fns-tz';
import PropTypes from 'prop-types';
import { useDispatch, useSelector } from 'react-redux';
import Box from '@mui/material/Box';
import Stack from '@mui/material/Stack';
import Button from '@mui/material/Button';
import Select from '@mui/material/Select';
import { Dialog, DialogContent, Typography } from '@mui/material';
import Popover from '@mui/material/Popover';
import MenuItem from '@mui/material/MenuItem';
import SaveIcon from '@mui/icons-material/Save';
import ToggleButton from '@mui/material/ToggleButton';
import ArrowDownwardIcon from '@mui/icons-material/ArrowDownward';
import ToggleButtonGroup from '@mui/material/ToggleButtonGroup';

import getOrderFilterOptions from '../../../util/GetOrderFilterOptions';
import {
  setOrderDate,
  setOrderTime,
  checkTimeSet,
} from '../../../redux/legacy/reducers/ducks/legacy/OrderDuck';
import useClasses from '../../../hooks/legacy/useClasses';
import useDesktopCheck from '../../../util/hooks/UseDesktopCheck';
import CustomSwipeableDrawer from '../../layout/CustomSwipeableDrawer';
import { useWhen } from '../../../hooks/useWhen';
import { useRestaurant } from '../../../hooks/useRestaurant';
import { acceptSelectedTime } from '../../../redux/legacy/reducers/ducks/new/Time/TimeReducer';
import { formatOrderTiming, timingKey } from '../../../util/orderTiming';
import { getBusinessTimezone } from '../../../util/businessTimezone';
import timePickerReadiness from '../../../util/timePickerReadiness';

import styles from './EditTimeForm.style';

function EditTimeForm({
  open,
  onClose,
  anchorEl,
  reorderTimeForm,
  handleTimeSubmit,
  selectedRestaurantDetails,
  allowGeneric,
}) {
  const classes = useClasses(styles);
  const dispatch = useDispatch();
  const isDesktop = useDesktopCheck();
  const { setSelectedTime, setSelectedDate, selectedTime } = useWhen();
  const { onSelectedRestaurantPage, selectedRestaurant } = useRestaurant();

  const { date, type, time, cartItems, reorderData } = useSelector(
    ({ order, time: timeState, reorder: reorderState }) => ({
      date: timeState?.selectedDate,
      type: order?.type,
      time: timeState?.selectedTime,
      cartItems: order?.cartItems,
      reorderData: order?.reorder?.order || reorderState?.order,
    })
  );

  const [formDateValue, setFormDateValue] = useState(date);
  const [formTimeValue, setFormTimeValue] = useState(time);
  const [givenDateOptions, setDateOptions] = useState([]);
  const [givenTimeOptions, setTimeOptions] = useState([]);
  const [clockTick, setClockTick] = useState(0);
  const initializedForOpen = useRef(false);
  // Marketplace filtering must never inherit a previously visited menu.
  let details = selectedRestaurantDetails;
  if (onSelectedRestaurantPage && !reorderTimeForm)
    details = selectedRestaurant;
  if (allowGeneric && !reorderTimeForm) details = null;
  const slotMode = reorderTimeForm
    ? reorderData?.type || type?.value
    : type?.value;
  const detailsFailed = useSelector(
    (state) => state.restaurant?.restaurantDetailsError
  );
  const detailsLoaded = useSelector(
    (state) => state.restaurant?.restaurantDetailsLoaded
  );
  const [optionsFailed, setOptionsFailed] = useState(false);
  const readiness = timePickerReadiness({
    details,
    generic: allowGeneric && !reorderTimeForm,
    mode: slotMode,
    failed: !reorderTimeForm && detailsFailed,
    loaded:
      reorderTimeForm || (details === selectedRestaurant && detailsLoaded),
  });
  const isAwaitingDetails = readiness !== 'ready' || optionsFailed;
  useEffect(() => setOptionsFailed(false), [details, slotMode, allowGeneric]);

  const dateTimeOptions = useMemo(() => {
    const key =
      formDateValue?.fullName === 'Today'
        ? givenDateOptions[0]?.key
        : formDateValue?.key;
    return givenTimeOptions?.[key]?.[slotMode] ?? [];
  }, [givenTimeOptions, givenDateOptions, formDateValue, slotMode]);

  const resetState = useCallback(() => {
    if (date) {
      setFormDateValue(date);
    }
    setFormTimeValue(time);
  }, [date, time]);
  useEffect(() => {
    resetState();
    initializedForOpen.current = false;
  }, [date, time, resetState]);

  // Derived loading flag used by both rendering and button disablement.
  useEffect(() => {
    if (!open) initializedForOpen.current = false;
    if (!open) return undefined;
    const timer = window.setInterval(
      () => setClockTick((tick) => tick + 1),
      60 * 1000
    );
    const onVisible = () => {
      if (document.visibilityState === 'visible')
        setClockTick((tick) => tick + 1);
    };
    document.addEventListener('visibilitychange', onVisible);
    return () => {
      window.clearInterval(timer);
      document.removeEventListener('visibilitychange', onVisible);
    };
  }, [open]);

  useEffect(() => {
    // When the form is shown outside the restaurant page (e.g. the global header
    // time-picker), we must wait for restaurantDetails to arrive from the API
    // before computing slots.  Without it, getOrderFilterOptions falls back to
    // the generic city-level config which does not account for the restaurant's
    // actual delivery delay or opening schedule, producing slots the API may
    // reject.
    //
    // Additionally, when details are cleared (e.g. navigating to a second
    // restaurant before its details have loaded), any previously computed slot
    // options and the selected form time must be discarded so stale data cannot
    // be submitted.
    if (isAwaitingDetails) {
      setTimeOptions([]);
      setDateOptions([]);
      setFormTimeValue(null);
      return;
    }

    if (!open) return;
    let options;
    try {
      options = getOrderFilterOptions(details);
    } catch (error) {
      setOptionsFailed(true);
      setTimeOptions([]);
      setDateOptions([]);
      setFormTimeValue(null);
      return;
    }
    const { dateOptions, timeOptions } = options;
    setDateOptions(dateOptions);
    const restoreFromWindow = !initializedForOpen.current;
    initializedForOpen.current = true;
    const timezone = getBusinessTimezone(details?.timezone);
    const selectedEpoch = Number(time?.value?.start);
    const windowDate =
      restoreFromWindow &&
      time?.type !== 'asap' &&
      Number.isFinite(selectedEpoch) &&
      !Number.isNaN(new Date(selectedEpoch * 1000).getTime())
        ? formatInTimeZone(
            new Date(selectedEpoch * 1000),
            timezone,
            'yyyy-MM-dd'
          )
        : null;
    const restoredDate =
      windowDate &&
      dateOptions.find(
        (option) =>
          formatInTimeZone(
            new Date(option.value * 1000),
            timezone,
            'yyyy-MM-dd'
          ) === windowDate
      );
    setFormDateValue(
      (previous) =>
        restoredDate ||
        (previous?.fullName === 'Today'
          ? dateOptions[0]
          : dateOptions.find(
              (option) =>
                option.key === previous?.key &&
                option.dayOfWeek === previous?.dayOfWeek
            ) || dateOptions[0])
    );
    setTimeOptions(timeOptions);
  }, [open, details, isAwaitingDetails, slotMode, clockTick, time, date]);

  useEffect(() => {
    if (time?.value === 'asap' && dateTimeOptions?.length) {
      setFormTimeValue(dateTimeOptions[0]);
    }
  }, [dateTimeOptions, time, date]);

  useEffect(() => {
    if (formDateValue) {
      const currentTimeLabel = formTimeValue?.label;

      let newInterval = null;
      if (currentTimeLabel) {
        const matchingIntervals = dateTimeOptions.filter(
          (interval) => interval.label === currentTimeLabel
        );
        newInterval = matchingIntervals[0] ?? null;
      }

      if (!newInterval) {
        newInterval = dateTimeOptions[0] ?? null;
      }

      if (!newInterval) {
        setFormTimeValue(null);
      } else {
        setFormTimeValue(newInterval);
      }
    }
  }, [formDateValue, dateTimeOptions]);

  const updateDate = (ev, value) => {
    if (value) {
      setFormDateValue(value);
    }
  };

  const updateTime = ({ target: { value } }) => {
    if (value) {
      setFormTimeValue(value);
    }
  };

  const handleSubmit = (event) => {
    event?.preventDefault();
    if (isAwaitingDetails || !formTimeValue || !dateTimeOptions.length) return;
    let fresh;
    try {
      fresh = getOrderFilterOptions(details);
    } catch (error) {
      setOptionsFailed(true);
      return;
    }
    const valid = fresh.timeOptions?.[formDateValue?.key]?.[slotMode]?.some(
      (slot) =>
        slot.type === formTimeValue.type &&
        (slot.type === 'asap' ||
          (slot.value?.start === formTimeValue.value?.start &&
            slot.value?.end === formTimeValue.value?.end))
    );
    if (!valid) {
      setClockTick((tick) => tick + 1);
      return;
    }
    if (reorderTimeForm) {
      handleTimeSubmit(formDateValue, formTimeValue);
      return;
    }
    dispatch(setOrderDate(formDateValue));
    // deprecated use useWhen hook instead
    dispatch(setOrderTime(formTimeValue));
    dispatch(checkTimeSet(true));

    // save time using useWhen hook
    setSelectedTime(formTimeValue);
    setSelectedDate(formDateValue);
    dispatch(acceptSelectedTime({ type: slotMode, time: formTimeValue }));

    onClose();
  };

  const stopPropagation = (ev) => ev.stopPropagation();

  const handleReset = () => {
    if (date) {
      setFormDateValue(date);
    }
    setFormTimeValue(time);
  };

  // A closed nested drawer must not expose a second, stale form in the DOM.
  if (!open) return null;

  const EditTimeBasicForm = (
    <form onSubmit={handleSubmit} data-test-id="editTimeForm">
      <Stack spacing={2}>
        <Typography role="status">
          {formatOrderTiming(formTimeValue, slotMode, details?.timezone)}
        </Typography>
        <ToggleButtonGroup
          aria-label="Order date"
          sx={{
            flexWrap: 'wrap',
            '& .MuiToggleButton-root': { minHeight: 44, flex: '1 1 3rem' },
          }}
          value={formDateValue}
          color="secondary"
          exclusive
          fullWidth
          onChange={updateDate}
          data-test-id="toggleButton"
        >
          {givenDateOptions?.map((option) => (
            <ToggleButton
              data-test-id="dateOption"
              className={
                formDateValue && formDateValue.label === option.label
                  ? classes.selectDateColor
                  : classes.UnSelectDateColor
              }
              key={option.dayOfMonth}
              aria-label={`${option.fullName}, ${option.dayOfMonth}`}
              value={option}
            >
              <Stack spacing={0}>
                <Typography variant="caption">{option.label}</Typography>

                <Typography>{option.dayOfMonth}</Typography>
              </Stack>
            </ToggleButton>
          ))}
        </ToggleButtonGroup>

        {isAwaitingDetails && readiness !== 'loading' && (
          <Box role="alert" data-test-id="timeSlotsError">
            <Typography>
              {readiness === 'request'
                ? 'Unable to load restaurant times. Check your connection, then reload the page.'
                : 'Order times are unavailable because the schedule or time configuration is invalid. Reload the page to get the latest settings. If this continues, contact support.'}
            </Typography>
            <Button
              data-test-id="reload-order-times"
              onClick={() => window.location.reload()}
            >
              Reload page
            </Button>
          </Box>
        )}
        {isAwaitingDetails && readiness === 'loading' && (
          <Box className="timeSelectionLoading" data-test-id="timeSlotsLoading">
            <Typography align="center" variant="subtitle2">
              Loading available times…
            </Typography>
          </Box>
        )}
        {!isAwaitingDetails &&
          (dateTimeOptions.length > 0 ? (
            <Box className={classes.formRow}>
              <Select
                sx={{ '& .MuiSelect-select': { whiteSpace: 'normal' } }}
                fullWidth
                variant="outlined"
                onChange={updateTime}
                value={formTimeValue}
                data-test-id="timeSelectWrapper"
                iconcomponent={ArrowDownwardIcon}
                classes={{
                  root: classes.selectRoot,
                  icon: classes.selectIcon,
                }}
                renderValue={() => (
                  <Box className={classes.option}>{formTimeValue?.label}</Box>
                )}
                inputProps={{
                  'data-test-id': 'timeSelect',
                  'aria-label': 'Order time window',
                }}
              >
                {dateTimeOptions?.map((option) => (
                  <MenuItem
                    value={option}
                    data-test-id="timeOption"
                    key={`date-time-option-${
                      option.value?.start || option.value
                    }`}
                  >
                    <Typography>{option?.label}</Typography>
                  </MenuItem>
                ))}
              </Select>
            </Box>
          ) : (
            <Box className="timeSelectionNotAvailable">
              <Typography align="center" variant="subtitle2">
                Sorry, not available for the selected day
              </Typography>
            </Box>
          ))}

        {time?.type !== 'asap' && time && !selectedTime && !formTimeValue && (
          <Typography>The selected time is no longer available</Typography>
        )}
        {timingKey('selection', slotMode, time) !==
          timingKey('selection', slotMode, formTimeValue) &&
        cartItems.length > 0 ? (
          <>
            <Typography>
              Attention: Changing the date or time may remove the items in your
              cart
            </Typography>
            <Box className={classes.buttons}>
              <Button
                className={classes.leftButton}
                variant="contained"
                fullWidth
                size="large"
                onClick={handleReset}
                data-test-id="resetButton"
              >
                Go back
              </Button>
              <Button
                variant="contained"
                fullWidth
                size="large"
                startIcon={<SaveIcon />}
                onClick={handleSubmit}
                data-test-id="updateButton"
                disabled={
                  isAwaitingDetails || !formTimeValue || !dateTimeOptions.length
                }
              >
                Confirm Time Change
              </Button>
            </Box>
          </>
        ) : (
          <Box className={classes.buttons}>
            <Button
              variant="contained"
              fullWidth
              size="large"
              startIcon={<SaveIcon />}
              onClick={handleSubmit}
              data-test-id="updateButton"
              disabled={
                isAwaitingDetails ||
                !formTimeValue ||
                dateTimeOptions.length === 0
              }
            >
              Update Order Time
            </Button>
          </Box>
        )}
        <Button
          onClick={() => {
            resetState();
            onClose();
          }}
          sx={{ minHeight: 44 }}
          data-test-id="cancel-order-time"
        >
          Cancel
        </Button>
      </Stack>
    </form>
  );

  const renderDesktop = () => (
    <Popover
      open={open}
      anchorEl={anchorEl}
      anchorOrigin={{
        vertical: 'bottom',
        horizontal: 'left',
      }}
      transformOrigin={{
        vertical: 'top',
        horizontal: 'left',
      }}
      onClose={onClose}
      data-test-id="order-type-filter"
      onClick={stopPropagation}
    >
      <Box sx={{ width: 375, padding: 4 }}>
        <Typography mb={2} variant="h6">
          Order Time
        </Typography>
        <div data-test-id="editTimeForm">{EditTimeBasicForm}</div>
      </Box>
    </Popover>
  );

  const renderMobile = () => (
    <CustomSwipeableDrawer open={open} onClose={onClose} title="Order Time">
      <Box
        role="dialog"
        aria-label="Order Time"
        sx={{ pb: 'env(safe-area-inset-bottom)' }}
      >
        {EditTimeBasicForm}
      </Box>
    </CustomSwipeableDrawer>
  );

  const reorderTimeFormDesktop = () => (
    <Dialog
      open={open}
      onClose={onClose}
      data-test-id="order-type-filter"
      fullWidth
      maxWidth="xs"
      sx={{ margin: 0 }}
    >
      <DialogContent
        className={classes.dialogWidth}
        sx={{ overflow: 'hidden' }}
      >
        <Box sx={{ width: 375, padding: 4 }}>
          <Typography mb={2} variant="h6">
            Order Time
          </Typography>
          <Typography variant="body1" mb={2}>
            {reorderData?.restaurant?.name} is not available for{' '}
            {reorderData?.type === 'delivery' ? 'Delivery' : 'Takeout'} at this
            time. Please select a new time to continue.
          </Typography>
          <Box data-test-id="editTimeForm">{EditTimeBasicForm}</Box>
        </Box>
      </DialogContent>
    </Dialog>
  );

  if (isDesktop) {
    if (!reorderTimeForm) {
      return renderDesktop();
    }
    return reorderTimeFormDesktop();
  }

  return renderMobile();
}

EditTimeForm.propTypes = {
  anchorEl: PropTypes.shape({}),
  open: PropTypes.bool,
  onClose: PropTypes.func,
  reorderTimeForm: PropTypes.bool,
  handleTimeSubmit: PropTypes.func,
  selectedRestaurantDetails: PropTypes.shape({}),
  allowGeneric: PropTypes.bool,
};

EditTimeForm.defaultProps = {
  anchorEl: null,
  selectedRestaurantDetails: null,
  open: false,
  onClose: () => null,
  reorderTimeForm: false,
  handleTimeSubmit: () => null,
  allowGeneric: false,
};

export default EditTimeForm;
